/**
 * Centralized Multilingual Core Keywords — 55+ Languages
 * Source of truth for the 3 primary keyword families:
 * 1. Centre d'appel / Call Center
 * 2. Offre d'emploi / Job Offer
 * 3. Freelancer
 *
 * Used by SEOHead, LongTailKeywordInjector, seo-descriptions, schemas, etc.
 * DO NOT modify translations — they are locked per the SEO strategy doc.
 */

export interface CoreKeywordSet {
  callCenter: string[];
  jobOffer: string[];
  freelancer: string[];
}

/**
 * Maps ISO 639 language codes → native translations of the 3 core keyword families.
 * Multiple variants per keyword are provided where applicable.
 */
export const MULTILINGUAL_CORE_KEYWORDS: Record<string, CoreKeywordSet> = {
  af: {
    callCenter: ['Oproepsentrum'],
    jobOffer: ['Werkaanbod', 'Vakature'],
    freelancer: ['Freelancer'],
  },
  sq: {
    callCenter: ['Qendra e thirrjeve'],
    jobOffer: ['Ofertë pune'],
    freelancer: ['Freelancer'],
  },
  de: {
    callCenter: ['Callcenter'],
    jobOffer: ['Stellenangebot'],
    freelancer: ['Freiberufler'],
  },
  am: {
    callCenter: ['የጥሪ ማእከል'],
    jobOffer: ['የሥራ ዕድል'],
    freelancer: ['ነጻ ሠራተኛ'],
  },
  en: {
    callCenter: ['Call centre', 'Call center'],
    jobOffer: ['Job offer', 'Job vacancy'],
    freelancer: ['Freelancer'],
  },
  ar: {
    callCenter: ['مركز اتصال'],
    jobOffer: ['عرض وظيفة', 'عرض عمل'],
    freelancer: ['عامل حر'],
  },
  hy: {
    callCenter: ['\u0536\u0561\u0576\u0563\u0565\u0580\u056B \u056F\u0565\u0576\u057F\u0580\u0578\u0576'],
    jobOffer: ['\u0531\u0577\u056D\u0561\u057F\u0561\u0576\u0584\u056B \u0561\u057C\u0561\u057B\u0561\u0580\u056F'],
    freelancer: ['\u0531\u0576\u056F\u0561\u056D \u0561\u0577\u056D\u0561\u057F\u0578\u0572'],
  },
  ast: {
    callCenter: ['Centru de llames'],
    jobOffer: ["Ufierta d'empléu"],
    freelancer: ['Trabayador independiente'],
  },
  eu: {
    callCenter: ['Deitzegune', 'Deialdi-zentroa'],
    jobOffer: ['Lan eskaintza'],
    freelancer: ['Freelance'],
  },
  bn: {
    callCenter: ['কল সেন্টার'],
    jobOffer: ['চাকরির অফার'],
    freelancer: ['মুক্তপেশা'],
  },
  be: {
    callCenter: ['Кол-центр'],
    jobOffer: ['Вакансія'],
    freelancer: ['Фрылансер'],
  },
  bh: {
    callCenter: ['काल सेंटर'],
    jobOffer: ['नौकरी के ऑफर'],
    freelancer: ['फ्रीलांसर'],
  },
  bg: {
    callCenter: ['Колцентър'],
    jobOffer: ['Оферта за работа'],
    freelancer: ['Фрийлансър'],
  },
  ca: {
    callCenter: ["Centre d'atenció telefònica"],
    jobOffer: ['Oferta de feina'],
    freelancer: ['Treballador independent'],
  },
  zh: {
    callCenter: ['呼叫中心'],
    jobOffer: ['招聘广告', '职位空缺'],
    freelancer: ['自由职业者'],
  },
  ko: {
    callCenter: ['콜 센터'],
    jobOffer: ['채용 공고'],
    freelancer: ['프리랜서'],
  },
  hr: {
    callCenter: ['Call centar'],
    jobOffer: ['Ponuda za posao'],
    freelancer: ['Slobodni suradnik'],
  },
  da: {
    callCenter: ['Callcenter'],
    jobOffer: ['Jobannonce'],
    freelancer: ['Freelance'],
  },
  es: {
    callCenter: ['Centro de llamadas'],
    jobOffer: ['Oferta de empleo'],
    freelancer: ['Trabajador autónomo'],
  },
  eo: {
    callCenter: ['Vokocentro'],
    jobOffer: ['Laboroferto'],
    freelancer: ['Sendependa kunlaboristo'],
  },
  et: {
    callCenter: ['Kõnekeskus'],
    jobOffer: ['Tööpakkumine'],
    freelancer: ['Vabakutseline'],
  },
  fi: {
    callCenter: ['Puhelinpalvelukeskus'],
    jobOffer: ['Työtarjous'],
    freelancer: ['Freelancer'],
  },
  fr: {
    callCenter: ["Centre d'appels"],
    jobOffer: ["Offre d'emploi"],
    freelancer: ['Freelancer', 'Travailleur indépendant'],
  },
  gl: {
    callCenter: ['Centro de chamadas'],
    jobOffer: ['Oferta de emprego'],
    freelancer: ['Freelancer'],
  },
  ka: {
    callCenter: ['სატელეფონო ცენტრი'],
    jobOffer: ['სამუშაოს შეთავაზება'],
    freelancer: ['ფრილანსერი'],
  },
  el: {
    callCenter: ['Κέντρο κλήσεων'],
    jobOffer: ['Αγγελία εργασίας'],
    freelancer: ['Ελεύθερος επαγγελματίας'],
  },
  he: {
    callCenter: ['מוקד טלפוני'],
    jobOffer: ['הצעת עבודה'],
    freelancer: ['פרילנסר'],
  },
  hi: {
    callCenter: ['कॉल सेंटर'],
    jobOffer: ['नौकरी की पेशकश'],
    freelancer: ['फ्रीलांसर'],
  },
  hu: {
    callCenter: ['Call center'],
    jobOffer: ['Állásajánlat'],
    freelancer: ['Szabadúszó'],
  },
  id: {
    callCenter: ['Pusat panggilan'],
    jobOffer: ['Lowongan kerja'],
    freelancer: ['Pekerja lepas'],
  },
  ga: {
    callCenter: ['Lárionad glaonna'],
    jobOffer: ['Tairiscint poist'],
    freelancer: ['Saorcheirdeach'],
  },
  is: {
    callCenter: ['Kallmiðstöð'],
    jobOffer: ['Atvinnuauglýsing'],
    freelancer: ['Freelancer'],
  },
  it: {
    callCenter: ['Call center'],
    jobOffer: ['Offerta di lavoro'],
    freelancer: ['Freelance'],
  },
  ja: {
    callCenter: ['コールセンター'],
    jobOffer: ['求人', '求人広告'],
    freelancer: ['フリーランサー'],
  },
  kk: {
    callCenter: ['Қоңырау орталығы'],
    jobOffer: ['Жұмыс ұсынысы'],
    freelancer: ['Фрилансер'],
  },
  ku: {
    callCenter: ['ناوەندی پەیوەندی'],
    jobOffer: ['ئاگاداری کار'],
    freelancer: ['سەربەستکار'],
  },
  lv: {
    callCenter: ['Zvanu centrs'],
    jobOffer: ['Darba piedāvājums'],
    freelancer: ['Brīvmākslinieks'],
  },
  lt: {
    callCenter: ['Skambučių centras'],
    jobOffer: ['Darbo pasiūlymas'],
    freelancer: ['Laisvai samdomas darbuotojas'],
  },
  mk: {
    callCenter: ['Центар за повици'],
    jobOffer: ['Понуда за работа'],
    freelancer: ['Фриленсер'],
  },
  ms: {
    callCenter: ['Pusat panggilan'],
    jobOffer: ['Tawaran pekerjaan'],
    freelancer: ['Pekerja bebas'],
  },
  mt: {
    callCenter: ['Ċentru tal-calls'],
    jobOffer: ['Offerta ta\' xogħol'],
    freelancer: ['Freelancer'],
  },
  nl: {
    callCenter: ['Callcenter'],
    jobOffer: ['Vacature'],
    freelancer: ['Freelancer'],
  },
  no: {
    callCenter: ['Kundesenter'],
    jobOffer: ['Stillingsannonse'],
    freelancer: ['Frilanser'],
  },
  ur: {
    callCenter: ['کال سینٹر'],
    jobOffer: ['نوکری کی پیشکش'],
    freelancer: ['فری لانسر'],
  },
  fa: {
    callCenter: ['مرکز تماس'],
    jobOffer: ['آگهی استخدام'],
    freelancer: ['کارمزدی'],
  },
  pl: {
    callCenter: ['Centrum telefoniczne'],
    jobOffer: ['Oferta pracy'],
    freelancer: ['Freelancer'],
  },
  pt: {
    callCenter: ['Central de atendimento'],
    jobOffer: ['Oferta de emprego'],
    freelancer: ['Freelancer'],
  },
  ro: {
    callCenter: ['Call centru'],
    jobOffer: ['Ofertă de angajare'],
    freelancer: ['Freelancer'],
  },
  ru: {
    callCenter: ['Колл-центр'],
    jobOffer: ['Вакансия'],
    freelancer: ['Фрилансер'],
  },
  sr: {
    callCenter: ['Позивни центар'],
    jobOffer: ['Ponuda za posao'],
    freelancer: ['Frilenser'],
  },
  sk: {
    callCenter: ['Call centrum'],
    jobOffer: ['Ponuka práce'],
    freelancer: ['Freelancer'],
  },
  sl: {
    callCenter: ['Klicni center'],
    jobOffer: ['Ponudba za delo'],
    freelancer: ['Freelancer'],
  },
  sv: {
    callCenter: ['Callcenter'],
    jobOffer: ['Jobbannons'],
    freelancer: ['Frilansare'],
  },
  sw: {
    callCenter: ['Kituo cha simu'],
    jobOffer: ['Tangazo la kazi'],
    freelancer: ['Freelancer'],
  },
  ta: {
    callCenter: ['அழைப்பு மையம்'],
    jobOffer: ['வேலை வாய்ப்பு'],
    freelancer: ['சுதந்திர வேலைத் தரர்'],
  },
  th: {
    callCenter: ['ศูนย์รับสาย'],
    jobOffer: ['ประกาศรับสมัครงาน'],
    freelancer: ['ฟรีแลนซ์'],
  },
  tr: {
    callCenter: ['Çağrı merkezi'],
    jobOffer: ['İş ilanı'],
    freelancer: ['Serbest meslek'],
  },
  uk: {
    callCenter: ['Кол-центр'],
    jobOffer: ['Вакансія'],
    freelancer: ['Фрилансер'],
  },
  vi: {
    callCenter: ['Trung tâm cuộc gọi'],
    jobOffer: ['Tin tuyển dụng'],
    freelancer: ['Freelancer'],
  },
  zu: {
    callCenter: ['Isikhungo socingo'],
    jobOffer: ['Isethulo somsebenzi'],
    freelancer: ['Umsebenzi ozimele'],
  },
  az: {
    callCenter: ['Çağrı mərkəzi'],
    jobOffer: ['İş elanı'],
    freelancer: ['Frilanser'],
  },
  // Bonus Maroc: Darija
  'ar-MA': {
    callCenter: ['call center', 'كول سنتر'],
    jobOffer: ['عرض شغل'],
    freelancer: ['فريلانسر', 'freelance'],
  },
  // Bonus Maroc: Tamazight
  ber: {
    callCenter: ['amasl n usiwel', 'call center'],
    jobOffer: ['asumer n umahil'],
    freelancer: ['amasay ilelli'],
  },
};

// Armenian (corrected)
MULTILINGUAL_CORE_KEYWORDS.hy = {
  callCenter: ['Զանգերի կենտրոն'],
  jobOffer: ['Աշխատանքի առաջարկ'],
  freelancer: ['Անկախ աշխատող'],
};

/**
 * Get the native keywords for a given language.
 * Returns all 3 families as flat array (useful for meta keywords injection).
 */
export function getCoreKeywordsFlat(lang: string): string[] {
  const set = MULTILINGUAL_CORE_KEYWORDS[lang] || MULTILINGUAL_CORE_KEYWORDS.en;
  return [...set.callCenter, ...set.jobOffer, ...set.freelancer];
}

/**
 * Get core keywords for a language, with regional fallback (e.g. fr-MA → fr).
 */
export function getCoreKeywordsForLocale(locale: string): string[] {
  if (MULTILINGUAL_CORE_KEYWORDS[locale]) return getCoreKeywordsFlat(locale);
  const base = locale.split('-')[0];
  return getCoreKeywordsFlat(base);
}

/**
 * Get ALL keywords from ALL languages as a flat array.
 * Useful for comprehensive meta keywords on global pages.
 */
export function getAllCoreKeywordsGlobal(): string[] {
  const all = new Set<string>();
  for (const set of Object.values(MULTILINGUAL_CORE_KEYWORDS)) {
    set.callCenter.forEach(k => all.add(k));
    set.jobOffer.forEach(k => all.add(k));
    set.freelancer.forEach(k => all.add(k));
  }
  return Array.from(all);
}

/**
 * Get keywords for the 16 core site languages only, as a flat array.
 */
export function getCoreKeywords16Languages(): string[] {
  const CORE_16 = ['en','fr','es','ar','de','it','pt','nl','pl','ru','zh','ja','tr','ko','sv','da'];
  const all = new Set<string>();
  for (const lang of CORE_16) {
    const set = MULTILINGUAL_CORE_KEYWORDS[lang];
    if (set) {
      set.callCenter.forEach(k => all.add(k));
      set.jobOffer.forEach(k => all.add(k));
      set.freelancer.forEach(k => all.add(k));
    }
  }
  return Array.from(all);
}
